import React, { useEffect, useState } from "react";
import styles from "./SearchParameters.module.scss";
import { ApplianceCategory } from "../../../model/appliance-category";
import { toast } from "react-toastify";
import FormWrapper from "../../../common/components/form-wrapper/FormWrapper";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { useForm } from "react-hook-form";
import CommonFileUploadInput from "../../../common/components/file-upload-input/CommonFileUploadInput";
import { FileFromObjectUrl } from "../../../model/common/file-from-object-url";
import SearchResultItem from "../../customer/solar-recommendation/components/add-appliances/components/SearchResultItem";
import {
	useCreateApplianceCategoryMutation,
	useDeleteApplianceCategoryMutation,
	useLazyGetApplianceCategoriesQuery,
	useUpdateApplianceCategoryMutation,
} from "../../../service/appliance-category";
import ApplianceCategoryItem from "./component/ApplianceCategoryItem";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";

type FormData = {
	id?: string;
	name: string;
	iconEdited: boolean;
	iconUrl: FileFromObjectUrl;
	icon: File;
	description: string;
};

const ApplianceCategoryManager: React.FC = () => {
	const dispatch = useDispatch();
	const [search, setSearch] = useState<string>("");
	const [debouncedSearch, setDebouncedSearch] = useState<string>("");
	const [categories, setCategories] = useState<ApplianceCategory[]>([]);
	const [isFormVisible, setIsFormVisible] = useState(false);
	const [isEdit, setIsEdit] = useState(false);

	const [getCategories, { isLoading, data }] = useLazyGetApplianceCategoriesQuery();
	const [createCategory] = useCreateApplianceCategoryMutation();
	const [updateCategory] = useUpdateApplianceCategoryMutation();
	const [deleteCategory] = useDeleteApplianceCategoryMutation();

    const defaultValues = {
        name: "",
        iconUrl: {
            name: "",
            url: "",
        },
        description: "",
    }
    useEffect(()=>{
        setCategories(data?? []);
    },[data])

	const fetchCategories = (searchQuery?: string) => {
        dispatch(showLoader());
        getCategories(searchQuery ? { query: searchQuery } : undefined)
            .unwrap()
            .catch(err => toast.error(err?.data?.title as string))
            .finally(() => dispatch(hideLoader()));
    };

    useEffect(() => {
        fetchCategories(debouncedSearch);
    }, [debouncedSearch]);


	const handleSubmit = async () => {
		try {
			const formData = getValues();
			const data = new FormData();

			data.append("Name", formData.name);
			data.append("Icon", formData.icon);
			data.append("Description", formData.description);

			if (isEdit && formData.id) {
                dispatch(showLoader());
				data.append("id", formData?.id);
				updateCategory(data)
					.unwrap()
					.then(res => {
						toast.success("Appliance Category updated successfully");
						// fetchCategories();
					})
					.catch(err => toast.error((err?.data?.title as string) ?? "An error happened"))
					.finally(() => dispatch(hideLoader()));
			} else {
                dispatch(showLoader())
				createCategory(data)
					.unwrap()
					.then(res => {
                        toast.success("Category added successfully");
					})
					.catch(err => toast.error(err?.data?.title as string))
					.finally(() => dispatch(hideLoader()));
			}

			setIsFormVisible(false);
		} catch (error) {
			toast.error(`Error ${isEdit ? "updating" : "creating"} category`);
		}
	};

	const handleDelete = async (category: ApplianceCategory) => {
        // console.log('category to delete', category);
		deleteCategory({id : category.id})
			.unwrap()
			.then(res => {
				toast.success("Appliance category deleted");
				// fetchCategories();
			})
			.catch(err => toast.error(err?.data?.title as string?? "Something went wrong"))
			.finally(() => dispatch(hideLoader()));
	};

    const openNewForm = () => {
		setIsEdit(false);
		reset({...defaultValues, iconUrl: undefined});
		setIsFormVisible(true);
	};

	const handleEdit = (category: ApplianceCategory) => {
		setIsEdit(true);
		setIsFormVisible(true);
		reset({
			id: category.id,
			name: category.name,
			description: category.description,
			iconEdited: false,
			iconUrl: {
				url: category?.imageUrl,
				name: category?.imageUrl?.split("/").pop()!,
			},
		});
	};

	const onFilesSelection = (files: File[]) => {
		if (files.length === 0) return;

		const fileToSend: FileFromObjectUrl = {
			name: files[0].name,
			url: URL.createObjectURL(files[0]),
		};
		setValue("icon", files[0]);
		setValue("iconUrl", fileToSend, { shouldValidate: true });
		setValue("iconEdited", true);
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		fetchCategories();
	}, []);

	const {
		register,
		watch,
		formState: { errors, isValid },
		setValue,
		getValues,
		reset,
	} = useForm<FormData>({
		defaultValues,
		mode: "onTouched",
	});

	return (
		<div className={styles.container}>
			<div className={styles.leftSide}>
				<FormWrapper
					titleSection={{
						title: "Appliance Categories",
						subtitle: "Search and manage appliance categories",
						buttonTitle: "Add",
					}}
					onSaveAndContinue={openNewForm}
				>
					<div className={styles.findApplianceContainer}>
						<div className={styles.searchContainer}>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M15.2825 14.7176L12.5001 11.9576C13.5801 10.6109 14.1032 8.90152 13.9617 7.18103C13.8201 5.46053 13.0248 3.85964 11.7392 2.70753C10.4536 1.55543 8.77541 0.939683 7.04974 0.986903C5.32408 1.03412 3.68209 1.74072 2.46141 2.96141C1.24072 4.18209 0.534124 5.82408 0.486903 7.54974C0.439683 9.27541 1.05543 10.9536 2.20753 12.2392C3.35964 13.5248 4.96053 14.3201 6.68103 14.4617C8.40152 14.6032 10.1109 14.0801 11.4576 13.0001L14.2176 15.7601C14.2873 15.8303 14.3702 15.8861 14.4616 15.9242C14.553 15.9623 14.651 15.9819 14.7501 15.9819C14.8491 15.9819 14.9471 15.9623 15.0385 15.9242C15.1299 15.8861 15.2128 15.8303 15.2825 15.7601C15.4177 15.6202 15.4933 15.4333 15.4933 15.2388C15.4933 15.0443 15.4177 14.8574 15.2825 14.7176ZM7.25005 13.0001C6.2117 13.0001 5.19666 12.6921 4.33331 12.1153C3.46995 11.5384 2.79704 10.7185 2.39968 9.75914C2.00232 8.79983 1.89836 7.74423 2.10093 6.72583C2.3035 5.70743 2.80351 4.77197 3.53774 4.03774C4.27197 3.30351 5.20743 2.8035 6.22583 2.60093C7.24423 2.39836 8.29983 2.50232 9.25914 2.89968C10.2185 3.29704 11.0384 3.96995 11.6153 4.83331C12.1921 5.69666 12.5001 6.7117 12.5001 7.75005C12.5001 9.14244 11.9469 10.4778 10.9624 11.4624C9.97779 12.4469 8.64244 13.0001 7.25005 13.0001Z"
									fill="#2E3336"
								/>
							</svg>
							<input placeholder="Search categories" onChange={e => setSearch(e.target.value)} />
						</div>
						<div className={styles.searchResultContainer}>
							{categories.map(category => (
								<ApplianceCategoryItem
									key={category.id}
									category={category}
									isAdded
									onEditApplianceHandler={() => handleEdit(category)}
									onDeleteApplianceHandler={() => handleDelete(category)}
								/>
							))}
						</div>
					</div>
				</FormWrapper>
			</div>
			{isFormVisible && (
				<div className={styles.rightSide}>
					<FormWrapper
						titleSection={{
							title: isEdit ? "Edit Category" : "Create Category",
							subtitle: "",
							buttonTitle: "Save Changes",
						}}
						onBack={() => {
							setIsFormVisible(false);
							reset(defaultValues);
						}}
						isNotValid={!isValid}
						onSaveAndContinue={handleSubmit}
					>
						<div className={styles.formSection}>
							<CommonFormFieldWrapper title="Category Name" required errors={getFormInputErrors(errors.name?.message)}>
								<input {...register("name", { required: "Category Name is required" })} placeholder="Enter Category Name" type="text" />
							</CommonFormFieldWrapper>
							<CommonFormFieldWrapper title="Description" required errors={getFormInputErrors(errors.description?.message)}>
								<input {...register("description", { required: "Description is required" })} placeholder="Enter a brief description" type="text" />
							</CommonFormFieldWrapper>

							<CommonFormFieldWrapper title="Category Image" required errors={getFormInputErrors(errors.iconUrl?.message)}>
								<div className={styles.fileInputContainer}>
									<CommonFileUploadInput
										maxFileSize={24}
										initialFiles={[watch("iconUrl.name")]}
										accept="image/*"
										registerInputProps={register("iconUrl", {
											required: "Category Image is required",
										})}
										onFilesSelect={onFilesSelection}
									/>
								</div>
							</CommonFormFieldWrapper>
						</div>
					</FormWrapper>
				</div>
			)}
		</div>
	);
};

export default ApplianceCategoryManager;
